import React, { useState } from 'react';
import { ArrowLeft, Clock, ChevronRight, Copy, Check } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { SeoHead } from '../components/SeoHead';
import { GUIDES, DIFFICULTY_COLOR, CATEGORY_COLOR } from './blog/guides';

// ── Code block with copy button ────────────────────────────────────────────
function CodeBlock({ lang, text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="relative rounded-sm overflow-hidden my-5" style={{ background: '#0a0a0a', border: '1px solid #242424' }}>
      <div className="flex items-center justify-between px-4 py-2" style={{ background: '#111', borderBottom: '1px solid #1e1e1e' }}>
        <span className="text-[10px] tracking-widest uppercase" style={{ color: 'rgba(229,229,229,0.3)' }}>{lang}</span>
        <button onClick={copy} className="flex items-center gap-1 text-[10px] transition-colors"
          style={{ color: copied ? '#c9a84c' : 'rgba(229,229,229,0.3)' }}>
          {copied ? <Check size={11} /> : <Copy size={11} />}
          {copied ? 'Copiato' : 'Copia'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-xs leading-relaxed" style={{ color: '#a8b4c8', fontFamily: "'JetBrains Mono', 'Fira Code', monospace", margin: 0 }}>
        <code>{text}</code>
      </pre>
    </div>
  );
}

// ── Content node renderer ──────────────────────────────────────────────────
function RenderNode({ node }) {
  const p = { fontSize: '14px', lineHeight: 1.85, color: 'rgba(229,229,229,0.72)', marginBottom: '14px' };
  switch (node.t) {
    case 'p':    return <p style={p}>{node.text}</p>;
    case 'h2':   return <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 400, color: '#e5e5e5', marginTop: '48px', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #1e1e1e' }}>{node.text}</h2>;
    case 'h3':   return <h3 style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(229,229,229,0.9)', marginTop: '28px', marginBottom: '10px' }}>{node.text}</h3>;
    case 'ul':   return <ul style={{ marginLeft: '20px', marginBottom: '14px', listStyle: 'disc' }}>{node.items.map((it, i) => <li key={i} style={p}>{it}</li>)}</ul>;
    case 'ol':   return <ol style={{ marginLeft: '20px', marginBottom: '14px', listStyle: 'decimal' }}>{node.items.map((it, i) => <li key={i} style={p}>{it}</li>)}</ol>;
    case 'code': return <CodeBlock lang={node.lang} text={node.text} />;
    case 'note': return (
      <div className="my-5 p-4 rounded-sm" style={{ background: 'rgba(201,168,76,0.08)', borderLeft: '3px solid #c9a84c' }}>
        <p style={{ ...p, marginBottom: 0, color: 'rgba(229,229,229,0.7)' }}><strong style={{ color: '#e5d4a1' }}>Nota:</strong> {node.text}</p>
      </div>);
    case 'warn': return (
      <div className="my-5 p-4 rounded-sm" style={{ background: 'rgba(239,68,68,0.07)', borderLeft: '3px solid #ef4444' }}>
        <p style={{ ...p, marginBottom: 0, color: 'rgba(229,229,229,0.7)' }}><strong style={{ color: '#fca5a5' }}>Attenzione:</strong> {node.text}</p>
      </div>);
    case 'tip':  return (
      <div className="my-5 p-4 rounded-sm" style={{ background: 'rgba(5,150,105,0.08)', borderLeft: '3px solid #059669' }}>
        <p style={{ ...p, marginBottom: 0, color: 'rgba(229,229,229,0.7)' }}><strong style={{ color: '#6ee7b7' }}>Consiglio:</strong> {node.text}</p>
      </div>);
    case 'step': return (
      <div className="flex gap-4 my-5">
        <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
          style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', marginTop: '2px' }}>
          {node.n}
        </div>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(229,229,229,0.9)', marginBottom: '6px' }}>{node.title}</p>
          <p style={p}>{node.body}</p>
        </div>
      </div>);
    case 'table': return (
      <div className="overflow-x-auto my-5">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr>{node.headers.map((h, i) => <th key={i} style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #2a2a2a', color: 'rgba(229,229,229,0.4)', fontWeight: 500, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>)}</tr>
          </thead>
          <tbody>{node.rows.map((row, ri) => (
            <tr key={ri}>{row.map((cell, ci) => <td key={ci} style={{ padding: '10px 12px', borderBottom: '1px solid #141414', color: 'rgba(229,229,229,0.7)', verticalAlign: 'top' }}>{cell}</td>)}</tr>
          ))}</tbody>
        </table>
      </div>);
    default: return null;
  }
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const guide = GUIDES.find(g => g.slug === slug);

  if (!guide) {
    return (
      <div style={{ background: '#080808', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#e5e5e5', fontFamily: "'DM Sans',sans-serif" }}>
        <p style={{ color: 'rgba(229,229,229,0.4)', fontSize: '14px' }}>Guida non trovata.</p>
        <a href="/blog" style={{ color: '#c9a84c', fontSize: '14px', marginTop: '12px' }}>← Torna alle Guide</a>
      </div>
    );
  }

  const diff = DIFFICULTY_COLOR[guide.difficulty] || DIFFICULTY_COLOR['Facile'];
  const catColor = CATEGORY_COLOR[guide.category] || '#c9a84c';
  const related = GUIDES.filter(g => g.slug !== slug && (g.category === guide.category || g.brand === guide.brand)).slice(0, 3);

  return (
    <div style={{ background: '#080808', color: '#e5e5e5', fontFamily: "'DM Sans', sans-serif", minHeight: '100vh' }}>
      <SeoHead page="blog" lang="it" theme="dark"
        customTitle={`${guide.title} — Shangrilux Guide`}
        customDescription={guide.excerpt} />

      {/* Nav */}
      <div className="sticky top-0 z-40 border-b" style={{ background: 'rgba(8,8,8,0.96)', backdropFilter: 'blur(20px)', borderColor: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <nav className="flex items-center gap-2 text-xs" style={{ color: 'rgba(229,229,229,0.4)' }}>
            <a href="/" className="hover:text-[#c9a84c] transition-colors" style={{ color: 'inherit' }}>Home</a>
            <ChevronRight size={11} />
            <a href="/blog" className="hover:text-[#c9a84c] transition-colors" style={{ color: 'inherit' }}>Guide</a>
            <ChevronRight size={11} />
            <span style={{ color: '#c9a84c' }}>{guide.brand}</span>
          </nav>
          <a href="/#demo" className="text-sm px-4 py-2 rounded-sm font-medium"
            style={{ background: '#c9a84c', color: '#000' }}>Demo Gratuita</a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex gap-12 items-start">

          {/* Main Content */}
          <article className="flex-1 min-w-0">
            {/* Header */}
            <div className="mb-12">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="text-[10px] font-semibold tracking-[3px] uppercase" style={{ color: catColor }}>
                  {guide.category}
                </span>
                <span style={{ color: 'rgba(229,229,229,0.2)' }}>·</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ background: diff.bg, color: diff.text, border: `1px solid ${diff.border}` }}>
                  {guide.difficulty}
                </span>
                <span className="text-[11px] flex items-center gap-1" style={{ color: 'rgba(229,229,229,0.35)' }}>
                  <Clock size={11} /> {guide.readTime}
                </span>
                <span className="text-[11px]" style={{ color: 'rgba(229,229,229,0.25)' }}>
                  Aggiornato: {new Date(guide.publishedAt).toLocaleDateString('it-IT', { year: 'numeric', month: 'long', day: 'numeric' })}
                </span>
              </div>

              <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(26px,4vw,42px)', fontWeight: 300, lineHeight: 1.2, color: '#e5e5e5', marginBottom: '16px' }}>
                {guide.title}
              </h1>
              <p className="text-base" style={{ color: 'rgba(229,229,229,0.5)', lineHeight: 1.7, maxWidth: '600px' }}>
                {guide.subtitle}
              </p>

              <div className="mt-6 h-px" style={{ background: `linear-gradient(to right, ${catColor}50, transparent)` }} />
            </div>

            {/* Content */}
            <div>
              {guide.content.map((node, i) => (
                <RenderNode key={i} node={node} />
              ))}
            </div>

            {/* Footer article */}
            <div className="mt-16 pt-8 border-t" style={{ borderColor: '#1e1e1e' }}>
              <div className="flex flex-wrap items-center gap-3">
                {guide.tags.map(tag => (
                  <span key={tag} className="text-[10px] px-2.5 py-1 rounded-full"
                    style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(229,229,229,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="hidden xl:block w-72 shrink-0">
            <div className="sticky top-24 space-y-6">

              {/* CTA box */}
              <div className="p-6 rounded-sm" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)' }}>
                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '18px', fontWeight: 400, color: '#e5e5e5', marginBottom: '8px' }}>
                  Prova l'integrazione {guide.brand}
                </p>
                <p className="text-xs mb-5" style={{ color: 'rgba(229,229,229,0.45)', lineHeight: 1.6 }}>
                  Accedi a un ambiente demo completo con {guide.brand} già configurato. 24 ore gratuite.
                </p>
                <a href="/#demo" className="block text-center text-sm font-semibold py-2.5 rounded-sm"
                  style={{ background: '#c9a84c', color: '#000' }}>
                  Inizia la Demo →
                </a>
              </div>

              {/* Guide list */}
              {related.length > 0 && (
                <div>
                  <p className="text-xs tracking-[3px] uppercase mb-4" style={{ color: 'rgba(229,229,229,0.3)' }}>Guide correlate</p>
                  <div className="space-y-3">
                    {related.map(g => (
                      <a key={g.slug} href={`/blog/${g.slug}`}
                        className="block p-3 rounded-sm transition-colors hover:border-[#c9a84c]"
                        style={{ background: '#0c0c0c', border: '1px solid #1a1a1a' }}>
                        <p className="text-xs font-medium" style={{ color: 'rgba(229,229,229,0.8)', marginBottom: '4px' }}>{g.title}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px]" style={{ color: CATEGORY_COLOR[g.category] }}>{g.brand}</span>
                          <span className="text-[10px]" style={{ color: 'rgba(229,229,229,0.25)' }}>{g.readTime}</span>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Back */}
              <a href="/blog" className="flex items-center gap-2 text-xs transition-colors hover:text-[#c9a84c]"
                style={{ color: 'rgba(229,229,229,0.3)' }}>
                <ArrowLeft size={12} /> Tutte le guide
              </a>
            </div>
          </aside>

        </div>
      </div>

      {/* Footer del sito */}
      <div className="border-t py-10" style={{ borderColor: '#141414' }}>
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '16px', fontWeight: 300, color: 'rgba(229,229,229,0.3)' }}>Shangrilux</span>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/', 'Home'], ['/pricing', 'Prezzi'], ['/blog', 'Guide'], ['/marketplace', 'Marketplace'], ['/developers', 'API']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.3)' }}>{l}</a>
            ))}
          </div>
          <div className="flex flex-wrap gap-5 justify-center">
            {[['/privacy', 'Privacy'], ['/cookie', 'Cookie'], ['/terms', 'Termini']].map(([h, l]) => (
              <a key={h} href={h} className="text-xs hover:opacity-70 transition-opacity" style={{ color: 'rgba(229,229,229,0.25)' }}>{l}</a>
            ))}
          </div>
          <p className="text-xs" style={{ color: 'rgba(229,229,229,0.2)' }}>
            © {new Date().getFullYear()} Shangrilux · Design by{' '}
            <a href="https://francescorusso.work" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Francesco Russo</a>
            {' · '}
            <a href="https://shangrilux.com" target="_blank" rel="noreferrer" className="hover:opacity-70 transition-opacity" style={{ color: 'rgba(201,168,76,0.5)' }}>Shangrilux</a>
          </p>
        </div>
      </div>
    </div>
  );
}
